import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Calculator, ChevronLeft, ChevronRight, Users, BookOpen, AlertCircle, Award, FileText, Clock } from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import api from '../components/api';

const CreateNoteConcour = () => {
  const { epreuveId } = useParams();
  const navigate = useNavigate();
  
  const [epreuve, setEpreuve] = useState(null);
  const [candidats, setCandidats] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [epreuveTerminee, setEpreuveTerminee] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  useEffect(() => {
    loadEpreuveData();
  }, [epreuveId]);

  const loadEpreuveData = async () => {
    try {
      setLoading(true);
      
      // Charger les données de l'épreuve
      console.log('🔍 Chargement de l\'épreuve:', epreuveId);
      const epreuveResponse = await api.get(`/concours/epreuves/${epreuveId}/`);
      console.log('✅ Épreuve chargée:', epreuveResponse.data);
      
      const epreuveData = epreuveResponse.data.data || epreuveResponse.data;
      setEpreuve(epreuveData);
      
      // Vérifier si l'épreuve a une session associée
      if (!epreuveData?.session_concours?.id) {
        console.error('❌ Aucune session associée à l\'épreuve');
        toast.error('Aucune session associée à cette épreuve');
        return;
      }
      
      const sessionId = epreuveData.session_concours.id;
      console.log('🔍 Chargement des candidats pour la session:', sessionId);
      
      // Charger les candidats de la session avec le bon paramètre
      const candidatsResponse = await api.get(`/concours/candidats/?session=${sessionId}&statut=CONFIRME`);
      console.log('📋 Réponse candidats:', candidatsResponse.data);
      
      const candidatsData = candidatsResponse.data.data || candidatsResponse.data.results || [];
      console.log('👥 Candidats trouvés:', candidatsData.length);
      
      if (!Array.isArray(candidatsData)) {
        console.error('❌ Format de données candidats invalide:', candidatsData);
        toast.error('Format de données candidats invalide');
        return;
      }
      
      setCandidats(candidatsData);
      
      if (candidatsData.length === 0) {
        console.warn('⚠️ Aucun candidat confirmé trouvé pour cette session');
        toast.warning('Aucun candidat confirmé trouvé pour cette session');
        setNotes([]);
        return;
      }
      
      // Charger les notes existantes
      console.log('🔍 Chargement des notes existantes pour l\'épreuve:', epreuveId);
      const notesResponse = await api.get(`/concours/epreuves/${epreuveId}/notes/`);
      console.log('📝 Réponse notes:', notesResponse.data);
      
      const notesData = notesResponse.data.data || notesResponse.data.results || [];
      console.log('📝 Notes existantes trouvées:', notesData.length);
      
      // Créer un objet de notes indexé par candidat ID
      const notesMap = {};
      notesData.forEach(note => {
        if (note.candidat && note.candidat.id) {
          notesMap[note.candidat.id] = note;
        }
      });

      // Créer la liste complète avec tous les candidats confirmés
      const notesCompletes = candidatsData.map(candidat => {
        const existingNote = notesMap[candidat.id];
        return {
          candidat: candidat,
          note: existingNote?.note || '',
          present: existingNote?.present !== undefined ? existingNote.present : true,
          commentaire: existingNote?.commentaire || '',
          id: existingNote?.id || null
        };
      });

      console.log('✅ Notes complètes créées:', notesCompletes.length);
      setNotes(notesCompletes);
      
      // Vérifier si l'épreuve est terminée
      setEpreuveTerminee(verifierEpreuveTerminee(epreuveData));
      
    } catch (error) {
      console.error('❌ Erreur lors du chargement:', error);
      console.error('❌ Détails de l\'erreur:', {
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url,
        message: error.message
      });
      
      if (error.response?.status === 404) {
        const url = error.config?.url;
        if (url?.includes('/epreuves/')) {
          toast.error('Épreuve introuvable');
        } else if (url?.includes('/candidats/')) {
          toast.error('Impossible de charger les candidats de cette session');
        } else {
          toast.error('Ressource introuvable');
        }
        navigate('/concours');
      } else if (error.response?.status === 401) {
        toast.error('Session expirée. Reconnectez-vous.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error('Problème de connexion réseau');
      } else {
        const errorMsg = error.response?.data?.message || error.message || 'Impossible de charger les données';
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifierEpreuveTerminee = (epreuveData) => {
    if (!epreuveData?.date_heure || !epreuveData?.duree_minutes) {
      return false;
    }
    
    const dateEpreuve = new Date(epreuveData.date_heure);
    const dureeMs = epreuveData.duree_minutes * 60 * 1000; // Convertir minutes en millisecondes
    const dateFinEpreuve = new Date(dateEpreuve.getTime() + dureeMs);
    const maintenant = new Date();
    
    return maintenant >= dateFinEpreuve;
  };

  const handleNoteChange = (index, field, value) => {
    if (!epreuveTerminee) {
      toast.warning('Impossible de modifier les notes : l\'épreuve n\'est pas encore terminée');
      return;
    }
    
    setNotes(prev => {
      const newNotes = [...prev];
      const globalIndex = (currentPage - 1) * itemsPerPage + index;
      newNotes[globalIndex] = {
        ...newNotes[globalIndex],
        [field]: value
      };
      return newNotes;
    });
  };

  const handleSave = async () => {
    if (!epreuveTerminee) {
      toast.error('Impossible de sauvegarder : l\'épreuve n\'est pas encore terminée');
      return;
    }
    
    // Validation : tous les candidats présents doivent avoir une note
    const candidatsPresents = notes.filter(noteItem => noteItem.present);
    const candidatsPresentsSansNote = candidatsPresents.filter(noteItem => 
      noteItem.note === '' || noteItem.note === null || noteItem.note === undefined
    );

    if (candidatsPresentsSansNote.length > 0) {
      toast.error(`${candidatsPresentsSansNote.length} candidat(s) présent(s) n'ont pas de note saisie. Veuillez compléter toutes les notes des candidats présents.`);
      return;
    }

    try {
      setSaving(true);
      
      // Préparer les données à envoyer (seulement les candidats présents avec notes)
      const notesToSave = candidatsPresents.map(noteItem => ({
        ...(noteItem.id && { id: noteItem.id }),
        candidat: noteItem.candidat.id,
        note: parseFloat(noteItem.note),
        present: noteItem.present,
        commentaire: noteItem.commentaire,
        epreuve: epreuveId
      }));

      // Envoyer les notes en lot
      const response = await api.post(`/concours/epreuves/${epreuveId}/notes/`, {
        notes: notesToSave
      });

      toast.success(response.data.message || 'Notes sauvegardées avec succès');
      navigate('/concours');
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData.errors && Array.isArray(errorData.errors)) {
          // Erreurs spécifiques pour certains candidats
          const errorCount = errorData.errors.length;
          toast.error(`${errorCount} erreur(s) de validation. Vérifiez les notes saisies.`);
        } else {
          toast.error(errorData.message || 'Données invalides. Vérifiez les notes saisies.');
        }
      } else if (error.response?.status === 401) {
        toast.error('Session expirée. Reconnectez-vous.');
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error('Problème de connexion réseau');
      } else {
        toast.error('Impossible de sauvegarder les notes');
      }
    } finally {
      setSaving(false);
    }
  };

  const calculateStats = () => {
    const candidatsPresents = notes.filter(n => n.present);
    const validNotes = candidatsPresents
      .filter(n => n.note !== '' && n.note !== null)
      .map(n => parseFloat(n.note));

    if (validNotes.length === 0) return null;

    const moyenne = validNotes.reduce((sum, note) => sum + note, 0) / validNotes.length;
    const min = Math.min(...validNotes);
    const max = Math.max(...validNotes);
    const reussite = validNotes.filter(note => note >= 10).length;

    return {
      moyenne: Math.round(moyenne * 100) / 100,
      min,
      max,
      reussite,
      total: validNotes.length,
      totalPresents: candidatsPresents.length,
      totalInscrits: notes.length,
      pourcentageReussite: Math.round((reussite / validNotes.length) * 100),
      tauxPresence: Math.round((candidatsPresents.length / notes.length) * 100)
    };
  };

  // Pagination
  const totalPages = Math.ceil(notes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentNotes = notes.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const stats = calculateStats();

  // Calcul des candidats présents sans note pour la validation
  const candidatsPresents = notes.filter(noteItem => noteItem.present);
  const candidatsPresentsSansNote = candidatsPresents.filter(noteItem => 
    noteItem.note === '' || noteItem.note === null || noteItem.note === undefined
  );

  const getEpreuveStatusInfo = () => {
    if (!epreuve?.date_heure || !epreuve?.duree_minutes) {
      return null;
    }
    
    const dateEpreuve = new Date(epreuve.date_heure);
    const dureeMs = epreuve.duree_minutes * 60 * 1000;
    const dateFinEpreuve = new Date(dateEpreuve.getTime() + dureeMs);
    const maintenant = new Date();
    
    if (maintenant < dateEpreuve) {
      return {
        type: 'future',
        message: 'Épreuve programmée',
        details: `Début prévu le ${dateEpreuve.toLocaleDateString('fr-FR')} à ${dateEpreuve.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
        color: 'bg-blue-50 border-blue-200 text-blue-800'
      };
    } else if (maintenant >= dateEpreuve && maintenant < dateFinEpreuve) {
      return {
        type: 'ongoing',
        message: 'Épreuve en cours',
        details: `Fin prévue le ${dateFinEpreuve.toLocaleDateString('fr-FR')} à ${dateFinEpreuve.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
        color: 'bg-orange-50 border-orange-200 text-orange-800'
      };
    } else {
      return {
        type: 'finished',
        message: 'Épreuve terminée',
        details: `Terminée le ${dateFinEpreuve.toLocaleDateString('fr-FR')} à ${dateFinEpreuve.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
        color: 'bg-green-50 border-green-200 text-green-800'
      };
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
            <span className="ml-3 text-gray-600">Chargement...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Titre et informations */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Gestion des notes - Concours
                </h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <BookOpen className="h-4 w-4 mr-1" />
                    {epreuve?.matiere?.nom} ({epreuve?.matiere?.code})
                  </div>
                  <div className="flex items-center">
                    <Users className="h-4 w-4 mr-1" />
                    {epreuve?.session_concours?.nom}
                  </div>
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 mr-1" />
                    {epreuve?.date_heure && new Date(epreuve.date_heure).toLocaleDateString('fr-FR')}
                  </div>
                  <div>
                    Coefficient: {epreuve?.coefficient}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statut de l'épreuve */}
        {(() => {
          const statusInfo = getEpreuveStatusInfo();
          if (!statusInfo) return null;
          
          return (
            <div className="mb-6">
              <div className={`rounded-lg border p-4 ${statusInfo.color}`}>
                <div className="flex items-center">
                  {statusInfo.type === 'future' && <Clock className="h-5 w-5 mr-2" />}
                  {statusInfo.type === 'ongoing' && <AlertCircle className="h-5 w-5 mr-2" />}
                  {statusInfo.type === 'finished' && <Award className="h-5 w-5 mr-2" />}
                  <div>
                    <div className="font-medium">{statusInfo.message}</div>
                    <div className="text-sm mt-1">{statusInfo.details}</div>
                    {!epreuveTerminee && (
                      <div className="text-sm mt-2 font-medium">
                        ⚠️ La saisie des notes sera disponible après la fin de l'épreuve
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Statistiques */}
        {stats && (
          <div className="mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calculator className="h-5 w-5 mr-2" />
                Statistiques
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg text-center border border-blue-200">
                  <div className="text-2xl font-bold text-blue-600">{stats.moyenne}/20</div>
                  <div className="text-sm text-blue-700">Moyenne générale</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg text-center border border-green-200">
                  <div className="text-2xl font-bold text-green-600">{stats.max}/20</div>
                  <div className="text-sm text-green-700">Note maximale</div>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-lg text-center border border-red-200">
                  <div className="text-2xl font-bold text-red-600">{stats.min}/20</div>
                  <div className="text-sm text-red-700">Note minimale</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg text-center border border-purple-200">
                  <div className="text-2xl font-bold text-purple-600">{stats.reussite}/{stats.total}</div>
                  <div className="text-sm text-purple-700">Candidats admis</div>
                </div>
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-lg text-center border border-yellow-200">
                  <div className="text-2xl font-bold text-yellow-600">{stats.pourcentageReussite}%</div>
                  <div className="text-sm text-yellow-700">Taux de réussite</div>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-lg text-center border border-indigo-200">
                  <div className="text-2xl font-bold text-indigo-600">{stats.tauxPresence}%</div>
                  <div className="text-sm text-indigo-700">Taux de présence</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table des notes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Liste des candidats ({notes.length})
              </h2>
              <div className="text-sm text-gray-500">
                Page {currentPage} sur {totalPages}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidat
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Présent
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Note (/20)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commentaire
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentNotes.map((noteItem, index) => (
                  <tr key={noteItem.candidat.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#202654] to-[#202654]/80 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {(noteItem.candidat.nom || 'N').charAt(0)}{(noteItem.candidat.prenom || 'P').charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {noteItem.candidat.nom_complet || `${noteItem.candidat.nom} ${noteItem.candidat.prenom}`}
                          </div>
                          <div className="text-sm text-gray-500">
                            N° {noteItem.candidat.numero_candidat}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={noteItem.present}
                        onChange={(e) => handleNoteChange(index, 'present', e.target.checked)}
                        disabled={!epreuveTerminee}
                        className={`h-4 w-4 text-[#202654] focus:ring-[#202654] border-gray-300 rounded ${
                          !epreuveTerminee ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="number"
                        min="0"
                        max="20"
                        step="0.5"
                        value={noteItem.note}
                        onChange={(e) => handleNoteChange(index, 'note', e.target.value)}
                        disabled={!noteItem.present || !epreuveTerminee}
                        className={`w-20 px-3 py-2 text-center border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all ${
                          !noteItem.present || !epreuveTerminee
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : noteItem.present && (noteItem.note === '' || noteItem.note === null || noteItem.note === undefined)
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-300'
                        }`}
                        placeholder={!epreuveTerminee ? "Épreuve en cours" : noteItem.present ? "Note" : "Absent"}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="text"
                        value={noteItem.commentaire}
                        onChange={(e) => handleNoteChange(index, 'commentaire', e.target.value)}
                        disabled={!epreuveTerminee}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all ${
                          !epreuveTerminee ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                        }`}
                        placeholder={!epreuveTerminee ? "Saisie désactivée" : "Ajouter un commentaire..."}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Affichage de {startIndex + 1} à {Math.min(endIndex, notes.length)} sur {notes.length} candidats
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Précédent
                  </button>
                  
                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                          currentPage === page
                            ? 'bg-[#202654] text-white shadow-sm'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Suivant
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bouton de sauvegarde en bas */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {candidatsPresentsSansNote.length > 0 && (
                <div className="flex items-center text-orange-600 text-sm">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {candidatsPresentsSansNote.length} candidat(s) présent(s) sans note
                </div>
              )}
              {candidatsPresentsSansNote.length === 0 && candidatsPresents.length > 0 && (
                <div className="flex items-center text-green-600 text-sm">
                  <Award className="h-4 w-4 mr-1" />
                  Toutes les notes des candidats présents sont complètes
                </div>
              )}
              <div className="text-sm text-gray-500">
                {candidatsPresents.length} présent(s) sur {notes.length} candidat(s)
              </div>
            </div>
            <button
              onClick={handleSave}
              disabled={saving || candidatsPresentsSansNote.length > 0 || !epreuveTerminee}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sauvegarde en cours...
                </>
              ) : !epreuveTerminee ? (
                <>
                  <Clock className="h-4 w-4 mr-2" />
                  Épreuve en cours - Saisie indisponible
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Sauvegarder les notes ({candidatsPresents.length})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreateNoteConcour;
